import { z } from 'zod';
import { baseListQuerySchema, sortOrderSchema, SortOrder } from './helpers';
import { knownTotalQuerySchema } from './known-total';
import { ColumnDataTypes, FilterOperators, FiltersSchema, TableResponseMetaSchema, createTableQuerySchema, } from './table';
import { PLATFORM_IDS } from './platform-registry';
/**
 * MCP Transport types
 */
export const mcpTransportSchema = z.enum([
    'http',
    'stdio_local',
    'stdio_repo',
    'stdio_package',
    // KIR-3940: ContextForge-native transports (gateway rows, slice 2b).
    'sse',
    'streamable_http',
]);
/**
 * Display labels for each MCP transport. Single source of truth shared by the
 * frontend render and the backend search builder (matches label + raw value).
 */
export const transportLabels = {
    http: 'HTTP',
    stdio_local: 'Command + Local Files',
    stdio_repo: 'Command + Repository',
    stdio_package: 'Command + Package',
    sse: 'SSE',
    streamable_http: 'Streamable HTTP',
};
// Org MCP status display labels — single source for the search expr + frontend options.
export const statusLabels = {
    running: 'Active',
    idle: 'Inactive',
};
/**
 * AgentMesh risk level of an MCP server (KIR-5108). AgentMesh owns the value;
 * Kirin only displays it. These are AgentMesh's five scan-status tag buckets
 * (`skillsrep/console.py` SCAN_STATUS_TAG_LABELS / web `scanStatusTag.ts`),
 * kept in AgentMesh's own order and spelling.
 */
export const mcpRiskLevelSchema = z.enum([
    'dangerous',
    'risky',
    'clean',
    'partial_scan',
    'unscanned',
]);
export const MCP_RISK_LEVELS = mcpRiskLevelSchema.options;
/** Display labels, matching AgentMesh's SCAN_STATUS_TAG_LABELS. */
export const mcpRiskLevelLabels = {
    dangerous: 'Dangerous',
    risky: 'Risky',
    clean: 'Clean',
    partial_scan: 'Partial scan',
    unscanned: 'Unscanned',
};
/** Tag colours, matching AgentMesh's SCAN_STATUS_TAG_COLORS. */
export const mcpRiskLevelColors = {
    dangerous: '#FF6467',
    risky: '#FDC700',
    clean: '#05DF72',
    partial_scan: '#FF8904',
    unscanned: '#737373',
};
/** An AgentMesh risk value plus when Kirin last synced it from AgentMesh. */
export const mcpServerRiskSchema = z.object({
    level: mcpRiskLevelSchema,
    lastSyncedAt: z.string().datetime(),
});
/**
 * Why a row has no AgentMesh risk value:
 * - `remote` — an HTTP server: no installed package to look up.
 * - `local` — local files or a repository checkout: no published package.
 * - `gateway` — a gateway-managed row: no per-endpoint package identity.
 * - `no-record` — a package AgentMesh holds no record for (or Kirin has not
 *   resolved its version yet).
 */
export const mcpRiskUnavailableReasonSchema = z.enum([
    'remote',
    'local',
    'gateway',
    'no-record',
]);
/** One installed package version under an MCP row, with its own verdict. */
export const mcpServerRiskVersionSchema = z.object({
    ecosystem: z.string(),
    packageName: z.string(),
    version: z.string(),
    userCount: z.number().int().min(0),
    risk: mcpServerRiskSchema.nullable(),
    riskFactors: z.array(z.string()),
    agentmeshUrl: z.string().nullable(),
});
/**
 * Risk detail card for one MCP row (KIR-5108): the row's value (worst
 * version), that version's top risk factors and report link, and every
 * installed version. No numeric score — AgentMesh does not publish one here.
 */
export const mcpServerRiskDetailSchema = z.object({
    name: z.string(),
    risk: mcpServerRiskSchema.nullable(),
    riskUnavailableReason: mcpRiskUnavailableReasonSchema.nullable(),
    riskFactors: z.array(z.string()),
    agentmeshUrl: z.string().nullable(),
    versions: z.array(mcpServerRiskVersionSchema),
});
/**
 * Client type - identifies the IDE/agent platform.
 * Derived from the platform registry so adding a new platform is a
 * compile-checked, single-location change.
 *
 * Used for:
 * - Kirin-Client-Type header identification
 * - MCP configuration sources
 * - Client activity tracking
 * - API key platform tracking
 */
export const ClientType = Object.fromEntries(PLATFORM_IDS.map(id => [id.charAt(0).toUpperCase() + id.slice(1), id]));
/**
 * Zod schema for ClientType validation.
 * Values are derived from the platform registry.
 */
export const clientTypeSchema = z.enum(PLATFORM_IDS);
/**
 * Client types the SERVER assigns as provenance markers — never accepted from
 * client-supplied input (KIR-3908 review). They stay in the registry/enum so the
 * `findings.client_type` column can store them and the UI can render their
 * `displayName`, but every ingest boundary rejects them so a client cannot
 * self-assert the provenance (e.g. label its own `/api/scans` as "via hook").
 * The server stamps them out-of-band (see `ClientMetadata.clientTypeOverride`).
 */
export const SERVER_ASSIGNED_CLIENT_TYPES = new Set([
    'claude_desktop_hook',
    // KIR-4703: browser-agent activity provenance is stamped server-side from the
    // authenticated ingest; a client must not self-assert "Browser AI Agent".
    'browser_agent',
    // KIR-4646: derived from the file path (`isSharedAgentConfigPath`); a client
    // must not stamp "Multi-IDE / Platform" on a file outside `.agents/skills`.
    'multi_tool',
]);
/**
 * Coerce an arbitrary, client-supplied value (e.g. a `Kirin-Client-Type`
 * header or `extraData.client.name` from a scan body) into a canonical
 * `PlatformId`, or `null` when it is not one.
 *
 * This is the shared ingest-boundary platform gate expressed as a contract
 * transform: data/handler layers parse untrusted input through this schema
 * instead of re-implementing validation, and the frontend can rely on the
 * exact same rule. Never throws — unrecognized OR server-assigned-only input
 * resolves to `null`.
 */
export const platformIdInputSchema = z
    .unknown()
    .transform((value) => {
    const result = clientTypeSchema.safeParse(value);
    if (!result.success)
        return null;
    return SERVER_ASSIGNED_CLIENT_TYPES.has(result.data) ? null : result.data;
});
/**
 * @deprecated Use clientTypeSchema instead
 */
export const mcpSourceSchema = clientTypeSchema;
/**
 * MCP Type (local vs remote origin)
 * - local: Server runs from local files (stdio_local or stdio_repo)
 * - remote: Server accessed via network (http) or from package registry (stdio_package)
 */
export const mcpTypeSchema = z.enum(['local', 'remote']);
/**
 * Row provenance (KIR-3940). `'gateway'` rows come from the ContextForge
 * union branch (slice 2b); `'endpoint'` rows are the pre-existing
 * config-reported rows. Optional on the wire so pre-union responses (every
 * row implicitly endpoint-sourced) keep parsing unchanged.
 */
export const mcpProvenanceSchema = z.enum(['gateway', 'endpoint']);
/**
 * Compact tool descriptor surfaced on an MCP inventory entry.
 * Mirrors MCPServerToolSummary on the agent and DB side.
 */
export const mcpServerToolSummarySchema = z.object({
    name: z.string(),
    description: z.string().optional(),
    // KIR-3940 gateway-control fields (slice 2b). Optional/nullable so
    // endpoint-sourced tool summaries keep parsing unchanged.
    id: z.string().nullable().optional(),
    blocked: z.boolean().optional(),
    effectiveBlocked: z.boolean().optional(),
    policyVersion: z.number().int().nullable().optional(),
});
// ============================================================================
// Inventory ingest (agent + IDE extension → POST /api/scans extraData)
// ============================================================================
/**
 * Transports accepted on the INGEST path.
 *
 * Deliberately narrower than `mcpTransportSchema`: `sse` and `streamable_http`
 * are ContextForge gateway-row transports (KIR-3940) that the backend
 * synthesises, never something a reporting client sends. Widening this to the
 * full enum would let a client self-assert a gateway transport.
 */
export const mcpIngestTransportSchema = z.enum([
    'http',
    'stdio_local',
    'stdio_repo',
    'stdio_package',
]);
/**
 * Resolved package identity for one MCP server (KIR-4842).
 *
 * This is the coordinate AgentMesh joins on. It is kept strictly separate from
 * `serverVersion` above, which is the version the server declares about itself
 * during the MCP `initialize` handshake and must never be used as identity: it
 * is a literal in the author's own source (`@modelcontextprotocol/server-memory`
 * answers `0.6.3` while the installed package is `2026.8.31`), it is
 * attacker-controlled, and behind the Docker MCP gateway every server answers
 * with the gateway's version. Both are stored; a mismatch is itself a signal.
 *
 * `ecosystem` and `resolutionMethod` are plain strings rather than enums ON
 * PURPOSE. The fleet will contain agents newer than this backend, and each
 * later slice of the epic introduces new `resolutionMethod` values. An enum
 * here would reject a newer agent's honest answer. The closed vocabulary lives
 * in the agent (`extensions/agent/pkg/mcp/identity`).
 *
 * Every field but the two above is nullish: an unresolved record is the normal
 * case, not a degraded one.
 */
export const mcpIdentityRecordSchema = z.object({
    /** Where the server's code comes from: npm | pypi | oci | http | local | git | unsupported. */
    ecosystem: z.string().min(1),
    /** Canonical package name within the ecosystem. Absent until a resolver parses it. */
    packageName: z.string().nullish(),
    /**
     * The PACKAGE SPEC as written in the config — never the argument vector.
     * The agent is responsible for keeping args, env values and filesystem paths
     * out of this field; real entries carry access tokens and home directories.
     */
    declaredSpec: z.string().nullish(),
    /** Whether the spec re-resolves on every spawn (bare name, dist-tag, range, untagged image). */
    floating: z.boolean().nullish(),
    /**
     * The version actually read from the installed artifact. Null/absent means no
     * resolver read one — never a guess, and never the registry's latest.
     */
    resolvedVersion: z.string().nullish(),
    /** How the version was obtained, or why it was not. */
    resolutionMethod: z.string().min(1),
    /**
     * When the VERSION was resolved, which is what makes a floating spec's answer
     * judgeable. The meaning depends on `resolutionMethod`: a method that read a
     * version reports when that resolution happened (for `npx-cache`, the run-once
     * lock file's mtime, which can be months old on a server still running), and
     * every other method falls back to when the agent looked. Comparing this
     * across rows with different methods compares two different clocks.
     */
    resolvedAt: z.coerce.date().nullish(),
    /** The join key, `ecosystem:name@version`. Absent whenever there is no version. */
    identityKey: z.string().nullish(),
});
/**
 * Where a version came from (KIR-4844). `installed` was read off an artifact on
 * an endpoint; `self-declared` is the MCP `initialize` handshake value, which
 * several official servers hardcode and a hostile server can set to anything.
 * Never interchangeable, hence the tag.
 */
export const mcpVersionSourceSchema = z.enum(['installed', 'self-declared']);
/**
 * One version a server reported, tagged with how much it is worth.
 *
 * Replaced a pair of parallel arrays, which made every consumer re-derive the
 * same precedence and let a column and its filter read different fields. One
 * tagged list makes `[0]` the best-evidenced version by construction.
 *
 * Only `version` and `source` are required: a self-declared entry has no
 * metadata, being an assertion rather than a measurement.
 */
export const mcpVersionEntrySchema = z.object({
    version: z.string().min(1),
    source: mcpVersionSourceSchema,
    /** How the version was obtained. `installed` entries only; open vocabulary. */
    method: z.string().optional(),
    /** The package spec as written in the config. `installed` entries only. */
    declaredSpec: z.string().optional(),
    /** Whether the spec re-resolves on every spawn. `installed` entries only. */
    floating: z.boolean().optional(),
    /**
     * When this version was resolved. Required to judge a floating entry, whose
     * version is only true as of a moment. `installed` entries only.
     */
    resolvedAt: z.string().datetime().optional(),
});
/**
 * One server as reported by a scanning client.
 *
 * Three producers post this shape and they do not agree on how much they fill
 * in: the Go agent sends `url`, `tools` and `identity`; the VS Code / Cursor
 * extension sends neither `url`, `tools` nor `identity`; older agents of any
 * kind predate `identity` entirely. All three must be accepted without error
 * for as long as those versions are in the fleet, so everything the agent alone
 * produces is optional.
 *
 * `.passthrough()` keeps unknown keys from failing the parse, so a NEWER agent
 * can add a field before this backend knows about it.
 */
export const mcpServerInfoSchema = z
    .object({
    configName: z.string().min(1),
    transport: mcpIngestTransportSchema,
    isRunning: z.boolean(),
    // Nullish rather than optional throughout: JSON has no `undefined`, so a
    // client that wants to say "not known" sends an explicit null.
    publisher: z.string().nullish(),
    serverName: z.string().nullish(),
    serverVersion: z.string().nullish(),
    url: z.string().nullish(),
    // Entry-level shape only. Per-tool sanitization stays in the service, which
    // drops unusable entries instead of rejecting the whole server.
    tools: z.array(z.unknown()).nullish(),
    // `.catch` is load-bearing, not defensive dressing. Without it a malformed
    // identity block (an empty `ecosystem`, an unparseable `resolvedAt`) fails
    // the WHOLE entry, the service drops that server from the validated list,
    // and reconcileMcpConfigs then deletes its stored row — so a bad enrichment
    // field would erase an MCP server that is genuinely installed. Identity is
    // best-effort on the agent side and must stay best-effort here: a block
    // that will not parse degrades to "no identity", which the upsert reads as
    // "preserve what is stored".
    //
    // Resilient, not silent. `.catch` has no logger, so the drop is reported
    // one level up: the ingest loop in mcp-configuration.service.ts re-parses
    // any identity block that was sent but came back undefined and warns with
    // the issue paths. Without that, a bad agent build could empty identity
    // across the fleet with nothing to alert on.
    identity: mcpIdentityRecordSchema.nullish().catch(undefined),
})
    .passthrough();
/**
 * The `extraData.mcpInventory` envelope.
 *
 * `servers` stays `unknown[]` so each entry can be validated individually: one
 * malformed server must not discard the rest of a scan's inventory. That
 * per-entry tolerance is long-standing ingest behaviour, and this path is
 * fire-and-forget, so a stricter parse would fail silently.
 */
export const mcpInventoryPayloadSchema = z.object({
    servers: z.array(z.unknown()),
});
/**
 * KIR-4482: org-level "New" signal and Allow/Block control for Inventory MCP
 * servers discovered on endpoints (gateway servers have their own control).
 * A server is New while it was first seen in the org within the New window and
 * no admin has reviewed it (allowing or blocking counts as review). The Block decision is
 * keyed by the server's resolved identity and enforced on endpoints by the
 * PreToolUse hook.
 */
export const mcpEnforcementViewSchema = z.enum(['new', 'blocked']);
/** Org-wide counts for the New banner and the New / Blocked chips. */
export const mcpEnforcementSummarySchema = z.object({
    newCount: z.number().int().min(0),
    blockedCount: z.number().int().min(0),
    newWindowDays: z.number().int().min(1),
});
/**
 * Path params of `PUT /organizations/:orgId/mcp-servers/:name/policy`: the
 * Inventory row (`MCPServerListItem.name`, the config key). Same shape as the
 * gateway's `PUT .../gateway/servers/:id/policy`.
 */
export const mcpServerPolicyParamsSchema = z.object({
    organizationId: z.string().min(1),
    name: z.string().trim().min(1).max(512),
});
/** Body of the Inventory Allow/Block toggle (mirrors the gateway toggle). */
export const mcpServerPolicyToggleBodySchema = z.object({
    blocked: z.boolean(),
});
export const mcpServerPolicyToggleResponseSchema = z.object({
    /** The Inventory row the decision was made from. */
    id: z.string(),
    blocked: z.boolean(),
    /** Time the decision was stored. */
    updatedAt: z.string().datetime(),
});
/** Audit metadata source tag for every Inventory MCP allow/block toggle. */
export const MCP_INVENTORY_CONTROL_AUDIT_SOURCE = 'inventory';
/**
 * MCP Server list item - returned from list endpoint
 */
export const mcpServerListItemSchema = z.object({
    // Row identity: the .mcp.json configuration key (KIR-2383).
    name: z.string(),
    // Handshake-reported server name (modal across the group); null if unknown.
    serverName: z.string().nullable(),
    /**
     * Every version reported, both sources, merged (KIR-4844): installed first
     * (newest first), then self-declared. `[0]` is what the Version column
     * headlines; the rest sit behind its `+N`. Empty is a real answer — a remote
     * or never-spawned server has none — and `resolutionMethods` says which.
     *
     * This RESHAPED an existing field (`string[]` → tagged objects) rather than
     * adding a sibling, which the KIR-4842 fields avoided by being `.optional()`.
     * Deliberate, and the cost is bounded: this is the internal
     * `/account/mcp-servers` contract, consumed only by our own SPA, and backend
     * and frontend ship from ONE release tag at ONE version
     * (`.github/workflows/production.yml` dispatches both from the same
     * `upload-kirin-gcp` call), so the two are never persistently skewed. The
     * residual case is a tab loaded before the deploy: `parseSchema` rejects the
     * list once, and a refresh fixes it. A transitional second field would have
     * meant two sources of truth for the Version column and two filters — the
     * thing this merge set out to remove.
     */
    versions: z.array(mcpVersionEntrySchema),
    publisher: z.string().nullable(),
    type: mcpTypeSchema, // Local or Remote (derived from transport)
    transport: mcpTransportSchema,
    sources: z.array(mcpSourceSchema), // All unique sources (cursor, claude, etc.)
    // KIR-3940: nullable because the gateway projection (slice 2b) sends null
    // for both on gateway-provenance rows — the union endpoint doesn't track
    // per-user attribution or live instance counts for gateway-managed servers.
    userCount: z.number().int().min(0).nullable(),
    runningCount: z.number().int().min(0).nullable(), // How many instances are currently running
    /**
     * How many of `runningCount`'s instances had their running state DERIVED from
     * a declared tool list rather than measured by an MCP handshake (KIR-4645).
     *
     * Claude Desktop connectors (Slack, Google Drive, …) are reachable only
     * through Claude Desktop, so the agent never handshakes them. When this
     * equals a non-zero `runningCount`, every running instance is derived and the
     * UI discloses that rather than implying a live probe.
     *
     * Optional so older callers and gateway-provenance rows, which carry no
     * instance attribution at all, need not supply it.
     */
    derivedRunningCount: z.number().int().min(0).nullable().optional(),
    firstSeenAt: z.string().datetime(),
    lastSeenAt: z.string().datetime(), // Most recent scan report for any row in this group
    url: z.string().nullable(), // Endpoint URL for HTTP transports (null when unknown)
    tools: z.array(mcpServerToolSummarySchema).nullable(), // Compact tool list (null when unknown)
    // --- KIR-3940 gateway-control fields (slice 2b union endpoint). All
    // optional/nullable so pre-union (endpoint-only) responses keep parsing. ---
    provenance: mcpProvenanceSchema.optional(),
    id: z.string().nullable().optional(),
    blocked: z.boolean().optional(),
    effectiveBlocked: z.boolean().optional(),
    policyVersion: z.number().int().nullable().optional(),
    // --- KIR-4842 resolved-identity fields. ---
    // Arrays, mirroring `versions` above, because a row in this list is a GROUP
    // spanning many users and machines that can legitimately disagree: one laptop
    // may have the package installed while another has only configured it. A
    // single "latest non-null" scalar would show one machine's version for the
    // whole group and quietly contradict `not-installed` being a real answer.
    // Optional so pre-upgrade clients keep parsing; empty until an agent that
    // reports identity has scanned, and there is no server-side backfill possible
    // because the data exists only on the laptop.
    ecosystems: z.array(z.string()).optional(),
    /**
     * How the versions were obtained, or why there are none. Group-level rather
     * than per-entry because it matters most when `versions` is EMPTY — it is
     * then the only thing that explains the absence.
     */
    resolutionMethods: z.array(z.string()).optional(),
    /**
     * One pick per USER in the group — each user's best-evidenced version — then
     * DEDUPED across users and ordered best-first (KIR-5042). The length is
     * therefore the number of DISTINCT VERSIONS PEOPLE ARE ON, not a head-count:
     * five users all on two versions give two entries.
     *
     * `versions` above lists every entry any row reported, which over-counts what
     * the Version column's `+N` is supposed to mean: a user with `2.0.0`
     * installed whose handshake also claims `1.0.0` is on ONE version, not two.
     * This field describes what is in use; `versions` describes evidence. Both
     * are needed — `versions` still carries the per-entry metadata the details
     * popover shows.
     *
     * Optional so pre-upgrade clients keep parsing, and empty on gateway-
     * provenance rows, which carry no per-user attribution at all. The cell falls
     * back to `versions` when it is absent.
     */
    userVersions: z.array(mcpVersionEntrySchema).optional(),
    // --- KIR-4482 New signal + Inventory block time. Optional: only sent on
    // endpoint rows of the org list while `mcp_inventory_enforcement` is on; the
    // decision itself is `blocked` / `effectiveBlocked` above. ---
    isNew: z.boolean().optional(),
    /** When the row's Inventory block was last decided; null when never. */
    blockUpdatedAt: z.string().datetime().nullable().optional(),
    // --- KIR-5108 AgentMesh risk. Optional: only sent while
    // `agentmesh_mcp_risk` is on for the organization. ---
    /**
     * The row's AgentMesh risk — the worst level across the row's installed
     * versions — or null when AgentMesh holds no record for any of them.
     */
    risk: mcpServerRiskSchema.nullable().optional(),
    /** Why `risk` is null; null when it is set. Drives the dash tooltip. */
    riskUnavailableReason: mcpRiskUnavailableReasonSchema.nullable().optional(),
    // NOTE: machineCount intentionally omitted - not actionable for users
});
/**
 * MCP Server list response with pagination
 */
export const mcpServerListResponseSchema = z.object({
    servers: z.array(mcpServerListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(100),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
});
/**
 * MCP Server user - for drill-down endpoint
 */
/**
 * One endpoint user's version in the drilldown (KIR-5042).
 *
 * A narrower shape than `mcpVersionEntrySchema`, for two reasons. The entry's
 * remaining fields (`declaredSpec`, `floating`, `resolvedAt`) describe one
 * measurement's trustworthiness and belong with the server's identity record,
 * not beside a person's name. And `method` is nullable-but-PRESENT rather than
 * optional: the response serializer compiles this schema to JSON Schema and
 * emits only declared properties, so an `.optional()` nested inside a
 * nullable-optional object is dropped from the body — it round-trips through
 * Zod and then vanishes on the wire. Observed, not theorised.
 */
export const mcpServerUserVersionSchema = z.object({
    version: z.string().min(1),
    source: mcpVersionSourceSchema,
    /**
     * How an installed version was obtained; null for a self-declared one, which
     * is an assertion rather than a measurement and so has no method.
     */
    method: z.string().nullable(),
});
export const mcpServerUserSchema = z.object({
    userId: z.string().uuid(),
    email: z.string().nullable(),
    name: z.string().nullable(),
    sources: z.array(mcpSourceSchema),
    /**
     * Distinct non-null handshake names reported for this row's `config_name`;
     * length >1 signals divergent self-reports under one key (KIR-2383).
     */
    serverNames: z.array(z.string()),
    isRunning: z.boolean(),
    lastSeenAt: z.string().datetime(),
    /**
     * THIS user's best-evidenced version for this row (KIR-5042): installed if
     * any of their machines has the artifact, otherwise their handshake claim.
     *
     * Null is a real answer and the common one — a remote server has no installed
     * copy to read, and a never-spawned one has nothing downloaded yet. The row's
     * `resolutionMethods` is what explains which.
     *
     * Optional so pre-upgrade clients keep parsing; the popup renders an em-dash
     * when it is absent or null.
     */
    version: mcpServerUserVersionSchema.nullable().optional(),
    // NOTE: machineCount intentionally omitted - not actionable for users
});
/**
 * MCP Server users response
 */
export const mcpServerUsersResponseSchema = z.object({
    serverName: z.string(),
    users: z.array(mcpServerUserSchema),
});
/** MCP row identifier — the `.mcp.json` `config_name` (KIR-2383). */
export const mcpServerNameSchema = z.string().min(1);
/** `GET /mcp-servers/risk?name=` — the risk card for one row (KIR-5108). */
export const mcpServerRiskQuerySchema = z.object({
    name: mcpServerNameSchema,
});
/**
 * Drilldown request body. Pass the row identity as `configName`. The legacy
 * `serverName` field is accepted for IDE-extension back-compat and will be
 * removed in a future release — when both are present, `configName` wins.
 *
 * The wire-level confusion this resolves: response-side
 * `mcpServerListItemSchema.serverName` carries the handshake name, while
 * request-side `serverName` historically carried the `config_name`. Use
 * `configName` for new code (KIR-2383).
 */
export const mcpServerUsersRequestBodySchema = z
    .object({
    /** Preferred row identifier — the `.mcp.json` `config_name`. */
    configName: mcpServerNameSchema.optional(),
    /** @deprecated Use `configName`. Kept for IDE-extension back-compat. */
    serverName: mcpServerNameSchema.optional(),
})
    .refine(body => Boolean(body.configName ?? body.serverName), {
    message: '`configName` is required (legacy `serverName` also accepted)',
    path: ['configName'],
});
/** Extract the row identity from a drilldown request body, preferring `configName`. */
export function getMcpConfigNameFromBody(body) {
    return (body.configName ?? body.serverName);
}
/**
 * Sort field options for MCP server list
 */
export const mcpSortFieldSchema = z.enum([
    'name',
    'version',
    'publisher',
    'type',
    'transport',
    'sources',
    'userCount',
    'runningCount', // Maps to "Status" column
    'tools', // Sorts by reported tool count
    'firstSeenAt',
    'lastSeenAt',
    'risk', // KIR-5108: AgentMesh risk, in AgentMesh console order
]);
/**
 * Sort order - re-exported from helpers for convenience.
 * Use the generic sortOrderSchema for new code.
 */
export const mcpSortOrderSchema = sortOrderSchema;
/**
 * Query schema for listing MCP servers.
 * Extends the base list query with MCP-specific sort and filter fields.
 */
export const mcpListQuerySchema = baseListQuerySchema.extend({
    sortBy: mcpSortFieldSchema.default('userCount'),
    sortOrder: sortOrderSchema.default(SortOrder.Desc),
    transport: mcpTransportSchema.optional(),
});
/**
 * Query parameters for listing MCP servers.
 * Optional fields for API client usage (no defaults).
 * Uses z.coerce for page/limit since query strings are always strings.
 */
export const listMcpServersParamsSchema = z.object({
    page: z.coerce.number().int().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(100).optional(),
    search: z.string().optional(),
    transport: mcpTransportSchema.optional(),
    sortBy: mcpSortFieldSchema.optional(),
    sortOrder: mcpSortOrderSchema.optional(),
    filters: FiltersSchema.optional(),
    knownTotal: knownTotalQuerySchema,
});
/**
 * Query parameters for the organization MCP list. Adds the KIR-4482 New /
 * Blocked chip (`enforcementView`), which only the org route serves.
 *
 * `enforcementView=blocked` also lists blocked servers no longer in the
 * Inventory window (still enforced on endpoints). They follow every inventory
 * row on every sort, ordered among themselves by last seen under a Last Seen
 * sort and by name otherwise; their `lastSeenAt` is the last report under the
 * name, or the block time when no report is left.
 */
export const organizationMcpListParamsSchema = listMcpServersParamsSchema.extend({
    enforcementView: mcpEnforcementViewSchema.optional(),
});
/**
 * Query parameters for listing personal MCP servers (legacy endpoint).
 *
 * Extends listMcpServersParamsSchema with explicit defaults for personal context:
 * - sortBy defaults to 'name' (userCount is meaningless for personal MCPs since it's always 1)
 * - sortOrder defaults to 'asc' for alphabetical ordering
 *
 * These defaults align with the personal MCP dashboard UI and provide
 * predictable behavior for API consumers.
 */
export const personalMcpListParamsSchema = listMcpServersParamsSchema.extend({
    // KIR-5108: AgentMesh risk is an org-list column gated per org; a personal
    // list has no flag to gate it on, so it cannot sort by it.
    sortBy: mcpSortFieldSchema.exclude(['risk']).default('name'),
    sortOrder: mcpSortOrderSchema.default(SortOrder.Asc),
});
// ============================================================================
// DataTable Integration: Filterable Fields & Filter Options
// ============================================================================
export const MCP_FILTERABLE_FIELDS = {
    NAME: 'name',
    VERSION: 'version',
    PUBLISHER: 'publisher',
    TYPE: 'type',
    TRANSPORT: 'transport',
    USERS: 'users',
    TOOLS: 'tools',
    STATUS: 'status',
    FIRST_SEEN_AT: 'firstSeenAt',
    LAST_SEEN_AT: 'lastSeenAt',
    PROVENANCE: 'provenance',
    RISK: 'risk',
};
/**
 * Per-column operator allow-list for org MCP filters. Narrows the DATE
 * data-type defaults so the First Seen column rejects the `equals`/`notEquals`
 * ("Is") operators — only range operators plus the value-less operators are valid.
 */
export const MCP_OPERATOR_OVERRIDES = {
    [MCP_FILTERABLE_FIELDS.FIRST_SEEN_AT]: [
        FilterOperators.GREATER_THAN,
        FilterOperators.LESS_THAN,
        FilterOperators.BETWEEN,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
    // KIR-3940: provenance is a branch selector on the backend union query
    // (slice 2b), not a general text/enum filter. ENUM columns don't support
    // `equals` (OPERATORS_BY_DATA_TYPE[ENUM] = [INCLUDES, EXCLUDES, IS_EMPTY,
    // IS_NOT_EMPTY]) and overrides can only narrow that set, so `includes` is
    // the only operator that's both meaningful and legal here.
    [MCP_FILTERABLE_FIELDS.PROVENANCE]: [FilterOperators.INCLUDES],
    // KIR-5108: "one or more risk levels". Values are mcpRiskLevelSchema.
    [MCP_FILTERABLE_FIELDS.RISK]: [FilterOperators.INCLUDES],
};
/**
 * Fields that have a backend filter-options endpoint (dynamic options).
 * Type, transport, and status have static options in the frontend.
 * firstSeenAt is a date field with no options dropdown.
 * tools serves distinct tool names extracted from each group's latest report.
 */
export const mcpFilterOptionFieldSchema = z.enum([
    MCP_FILTERABLE_FIELDS.NAME,
    MCP_FILTERABLE_FIELDS.VERSION,
    MCP_FILTERABLE_FIELDS.PUBLISHER,
    MCP_FILTERABLE_FIELDS.USERS,
    MCP_FILTERABLE_FIELDS.TOOLS,
]);
export const mcpFilterOptionsQuerySchema = z.object({
    field: mcpFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
/**
 * Rich filter option item with value, label, and optional metadata.
 * Used for complex filter options like users where ID alone isn't displayable.
 */
export const filterOptionItemSchema = z.object({
    value: z.string(),
    label: z.string().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
export const mcpFilterOptionsResponseSchema = z.object({
    field: mcpFilterOptionFieldSchema,
    options: z.union([z.array(z.string()), z.array(filterOptionItemSchema)]),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(0),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
const MCP_FILTER_COLUMN_DATA_TYPES = {
    [MCP_FILTERABLE_FIELDS.NAME]: ColumnDataTypes.STRING,
    [MCP_FILTERABLE_FIELDS.VERSION]: ColumnDataTypes.STRING,
    [MCP_FILTERABLE_FIELDS.PUBLISHER]: ColumnDataTypes.STRING,
    [MCP_FILTERABLE_FIELDS.TYPE]: ColumnDataTypes.ENUM,
    [MCP_FILTERABLE_FIELDS.TRANSPORT]: ColumnDataTypes.ENUM,
    [MCP_FILTERABLE_FIELDS.USERS]: ColumnDataTypes.ENUM,
    [MCP_FILTERABLE_FIELDS.TOOLS]: ColumnDataTypes.ENUM,
    [MCP_FILTERABLE_FIELDS.STATUS]: ColumnDataTypes.ENUM,
    [MCP_FILTERABLE_FIELDS.FIRST_SEEN_AT]: ColumnDataTypes.DATE,
    [MCP_FILTERABLE_FIELDS.LAST_SEEN_AT]: ColumnDataTypes.DATE,
    [MCP_FILTERABLE_FIELDS.PROVENANCE]: ColumnDataTypes.ENUM,
    [MCP_FILTERABLE_FIELDS.RISK]: ColumnDataTypes.ENUM,
};
/**
 * Table query schema for the MCP list endpoint.
 * Handles bracket-notation filter params (e.g. name[includes]=foo,bar).
 * Used by the backend route handler.
 */
const mcpTableQueryBaseSchema = baseListQuerySchema.extend({
    sortBy: mcpSortFieldSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
    filters: FiltersSchema.optional(),
    knownTotal: knownTotalQuerySchema,
    // KIR-4482: the New / Blocked chips. Absent = All servers.
    enforcementView: mcpEnforcementViewSchema.optional(),
});
export const mcpTableQuerySchema = createTableQuerySchema(mcpTableQueryBaseSchema, MCP_FILTER_COLUMN_DATA_TYPES, MCP_OPERATOR_OVERRIDES);
/**
 * Gateway sync state surfaced on the MCP table response `meta` (KIR-3940,
 * slice 2b). UI assumes a single gateway for slice 1 (per design doc); all
 * fields are nullable/absent-safe so pre-union responses keep parsing.
 */
export const mcpGatewaySyncStateSchema = z.object({
    lastContactAt: z.string().datetime().nullable(),
    policyVersion: z.number().int().nullable(),
    appliedPolicyVersion: z.number().int().nullable(),
    gatewayCount: z.number().int().min(0),
});
/**
 * Table response schema for MCP server list (DataTable format).
 */
export const mcpTableResponseSchema = z.object({
    data: z.array(mcpServerListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
    meta: TableResponseMetaSchema.extend({
        gatewaySync: mcpGatewaySyncStateSchema.optional(),
        enforcementSummary: mcpEnforcementSummarySchema.optional(), // KIR-4482
    }),
});
//# sourceMappingURL=mcp.js.map